import {
  ABExperimentName,
  DEFAULT_GROUP,
  DeploymentMap,
  DeploymentMetadata,
  FeatureFlag,
  SignupFlowsOnNewDesign,
  SignupFlowType,
} from '@signup/shared';
import { SignupParams } from './ParseQueryParams';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const typedWindowInstance = window as any as {
  trial?: {
    clientVersion?: string;
    recaptchaSiteKey?: string;
    stripePublishableKey?: string;
    isoCode?: string;
    features?: { [key: string]: boolean };
    experimentGroups?: { [key: string]: string };
    coordinates?: [number, number] | [];
    referrerUrl?: string;
    deployments?: DeploymentMap;
    signupFlowType?: SignupFlowType;
  };
  build?: {
    num?: number;
    time?: string;
    version?: string;
    env?: 'test' | 'production' | 'development';
    deploymentGroup?: 'PROD_DEPLOYMENTS' | 'DEV_DEPLOYMENTS';
  };
  dataLayer: Array<{ event: string } | { 'gtm.start': number; event: 'gtm.js' }>;
};

export function isFeatureEnabled(feature: FeatureFlag): boolean {
  return typedWindowInstance?.trial?.features?.[feature] === true;
}

export function isCortexWaitlistEnabled(signupParams?: SignupParams): boolean {
  // Check for query parameter override in canary environment only
  const urlParams = new URLSearchParams(window.location.search);
  const waitlistOverride = urlParams.get('cortexWaitlist');

  // Only allow override in canary environment
  if (waitlistOverride !== null && isCanaryEnvironment()) {
    return waitlistOverride === 'true';
  }

  if (signupParams?.token && signupParams.token.trim().length > 0) {
    return false;
  }

  // Fall back to normal feature flag
  return isFeatureEnabled(FeatureFlag.FF_ENABLE_CORTEX_CODE_WAITLIST);
}

function isCanaryEnvironment(): boolean {
  // Check if we're in canary environment (same logic as recaptcha skip)
  if (typedWindowInstance?.build?.deploymentGroup === 'DEV_DEPLOYMENTS') {
    return true;
  }

  // Check for canary beach cookie
  const beachCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('snowflake_growth_channel='))
    ?.split('=')[1];

  return beachCookie === 'canary';
}

export function getIsoCode(): string {
  return typedWindowInstance?.trial?.isoCode || '';
}

export function getReferrerUrl(): string {
  return typedWindowInstance?.trial?.referrerUrl || '';
}

export function getRecaptchaSiteKey(): string {
  return typedWindowInstance?.trial?.recaptchaSiteKey || '';
}

export function getDeploymentGroup(): 'PROD_DEPLOYMENTS' | 'DEV_DEPLOYMENTS' {
  return typedWindowInstance?.build?.deploymentGroup || 'PROD_DEPLOYMENTS';
}

export function getCoordinates(): [number, number] | [] {
  return typedWindowInstance?.trial?.coordinates || [];
}

export function getBuildEnv(): 'test' | 'production' | 'development' {
  return typedWindowInstance?.build?.env || 'production';
}

export function getExperimentGroup(experiment: ABExperimentName): string {
  return typedWindowInstance?.trial?.experimentGroups?.[experiment] || DEFAULT_GROUP;
}

export function usingNewSignupForm(): boolean {
  const signupFlowType = typedWindowInstance?.trial?.signupFlowType;
  return Boolean(signupFlowType && SignupFlowsOnNewDesign.includes(signupFlowType));
}

export function getDeployments(): DeploymentMap {
  return Object.freeze((typedWindowInstance?.trial?.deployments as DeploymentMap) || {});
}

export function getDeploymentsList(): readonly DeploymentMetadata[] {
  return Object.freeze(Object.values(getDeployments()));
}

export function getClientVersion(): string {
  return typedWindowInstance?.trial?.clientVersion || '';
}

export function getDataLayer(): Array<
  { event: string } | { 'gtm.start': number; event: 'gtm.js' }
> {
  return typedWindowInstance.dataLayer;
}

export function getSignupFlowTypeWithoutListener(): SignupFlowType {
  return typedWindowInstance?.trial?.signupFlowType || SignupFlowType.Unknown;
}

export function setSignupFlowType(signupFlowType: SignupFlowType): void {
  if (typedWindowInstance?.trial?.signupFlowType) {
    typedWindowInstance.trial.signupFlowType = signupFlowType;
    window.dispatchEvent(new Event('signupFlowTypeChange'));
  }
}

export function getExperimentGroups(): { [key: string]: string } {
  return typedWindowInstance?.trial?.experimentGroups || {};
}

export function getFeatureFlags(): { [key: string]: boolean } {
  return typedWindowInstance?.trial?.features || {};
}

export function getStripePublishableKey(): string | undefined {
  const key = typedWindowInstance?.trial?.stripePublishableKey;
  return key && key.length > 0 ? key : undefined;
}
